import styled from 'styled-components';
import {
  compose,
  space,
  SpaceProps,
  typography,
  TypographyProps,
  shadow,
  ShadowProps,
  layout,
  LayoutProps,
  borders,
  BordersProps,
  flexbox,
  position
} from 'styled-system';
import { colorLuminance } from '../../../utils/colors';

type StyledRadioboxProps = SpaceProps &
  TypographyProps &
  ShadowProps &
  LayoutProps &
  BordersProps &
  React.InputHTMLAttributes<HTMLInputElement>;

export interface IStyledRadioboxProps extends StyledRadioboxProps {
  /**
   * @default false
   */
  disabled?: boolean;
}

export const StyledRadiobox = styled.input.attrs(({ defaultChecked }) => ({
  type: 'radio' as string,
  defaultChecked
}))<IStyledRadioboxProps>`
  order: 1;
  ${({ theme, disabled }) => `
    position: relative;
    appearance: none;
    z-index: 1;
    height: 18px;
    width: 18px;
    min-width: 18px;
    border: 1px solid;
    border-color: ${theme.colors.form.input.border.enabled};
    border-radius: 50%;
    background-color: ${theme.colors.grayScale[0]};
    transition: all 0.1s ease-in;

    &::after {
      content: '';
      border-radius: 50%;
      width: 10px;
      height: 10px;
      background-color: ${theme.colors.form.input.border.focus};
    }

    &:focus {
      outline: none;
      border-color: ${theme.colors.form.input.border.focus};
      box-shadow: 0px 0px 0px 1px ${colorLuminance(
        theme.colors.form.input.border.focus,
        0.2
      )};
    }

    &:active {
      outline: none;
      border-color: ${theme.colors.form.input.border.focus};
    }

    &:checked {
      border-color: ${theme.colors.form.input.border.focus};

      &::after {
        top: 3px;
        left: 3px;
        position: absolute;
      }
    }

    &:hover {
      ${!disabled && `border-color: ${theme.colors.form.input.border.focus};`}
      cursor: pointer;
    }

    &:hover:checked {
      border-color: ${colorLuminance(
        theme.colors.form.input.border.focus,
        0.2
      )};

      &::after {
        background-color: ${colorLuminance(
          theme.colors.form.input.border.focus,
          0.2
        )};
      }
    }

    &:disabled {
      cursor: not-allowed;
      border-color: ${theme.colors.form.input.border.disabled};

      &::after {
        background-color: ${theme.colors.form.input.border.disabled};
      }
    }

    &:checked::-ms-check {
      color: ${theme.colors.form.input.border.focus};
      border-color: ${theme.colors.form.input.border.focus};
      &:hover {
        border-color: ${colorLuminance(
          theme.colors.form.input.border.focus,
          0.2
        )};
        color: ${colorLuminance(theme.colors.form.input.border.focus, 0.2)};
      }
    }

    &:hover:enabled::-ms-check {
      border-color: ${theme.colors.form.input.border.focus};
    }


  `}

  ${compose(typography, space, shadow, layout, borders, flexbox, position)}
`;

export default { StyledRadiobox };
